import {
	ConfigurationBlock,
	getPlatformTextFromProctoringOptions,
	PlatformBlock,
	ThirdPartyBlock,
	ThirdPartyPlatform,
	VerityInstituteSettings,
} from "@verity/components";
import {
	Application,
	Extension,
	OptionRelatedInstituteSettings,
	ProctoringAssessmentConfigurationV1,
	ProctoringOption,
} from "@verity/proctoring-option/types";
import { CoreLoadingPage } from "@verity/components/core";
import styles from "./ConfigurationPanel.module.css";

function convertProctoringAssessmentConfigurationV1toProctoringOption(
	proctoringAssessmentConfiguration?: Partial<ProctoringAssessmentConfigurationV1>,
) {
	const proctoringOptions: Partial<ProctoringOption> = {
		secondaryVideoRecordingMode: proctoringAssessmentConfiguration?.secondaryVideoRecordingMode,
		videoRecordingMode: proctoringAssessmentConfiguration?.videoRecordingMode,
		audioRecordingMode: proctoringAssessmentConfiguration?.audioRecordingMode,
		recordScreen: proctoringAssessmentConfiguration?.recordScreen,
		scanRoom: proctoringAssessmentConfiguration?.scanRoom,
		recordWebTraffic: proctoringAssessmentConfiguration?.recordWebTraffic,
		detectSidePanel: proctoringAssessmentConfiguration?.detectSidePanel,
		oneScreenEnforcementMode: proctoringAssessmentConfiguration?.oneScreenEnforcementMode,
		disableNewTab: proctoringAssessmentConfiguration?.disableNewTab,
		closeOpenTabs: proctoringAssessmentConfiguration?.closeOpenTabs,
		tabWhiteList: proctoringAssessmentConfiguration?.tabWhiteList,
		disableClipboardMode: proctoringAssessmentConfiguration?.disableClipboardMode,
		enableGuidedAccessMode: proctoringAssessmentConfiguration?.enableGuidedAccessMode,
		restrictHandoffMode: proctoringAssessmentConfiguration?.restrictHandoffMode,
		showRecordingPanel: proctoringAssessmentConfiguration?.showRecordingPanel,
		idVerification: proctoringAssessmentConfiguration?.idVerification,
		idVerificationEnableAi: proctoringAssessmentConfiguration?.idVerificationEnableAi,
		faceTrackingMode: proctoringAssessmentConfiguration?.faceTrackingMode,
		objectDetectionMode: proctoringAssessmentConfiguration?.objectDetectionMode,
		enforceLockdown: proctoringAssessmentConfiguration?.enforceLockdown,
		enforceMobile: proctoringAssessmentConfiguration?.enforceMobile,
		isExtension: proctoringAssessmentConfiguration?.isExtension,
		isLockdown: proctoringAssessmentConfiguration?.isLockdown,
		isMobile: proctoringAssessmentConfiguration?.isMobile,
		isChromeOs: proctoringAssessmentConfiguration?.isChromeOs ?? proctoringAssessmentConfiguration?.isLockdown,
		recordClipboard: proctoringAssessmentConfiguration?.recordClipboard,
		recordInput: proctoringAssessmentConfiguration?.recordInput,
		excludedWebTrafficRecordingUrls: proctoringAssessmentConfiguration?.excludedWebTrafficRecordingUrls,
		disableSpellcheck: proctoringAssessmentConfiguration?.disableSpellcheck,
		disablePrinting: proctoringAssessmentConfiguration?.disablePrinting,
		disableRightClick: proctoringAssessmentConfiguration?.disableRightClick,
		extensionBlockMode: proctoringAssessmentConfiguration?.extensionBlockMode,
		allowedExtensionsList: proctoringAssessmentConfiguration?.allowedExtensionsList,
		logInstalledExtensions: proctoringAssessmentConfiguration?.logInstalledExtensions,
		tabBlockList: proctoringAssessmentConfiguration?.tabBlockList,
		hideOtherWindows: proctoringAssessmentConfiguration?.hideOtherWindows,
		keyboardShortcutBlockList: proctoringAssessmentConfiguration?.keyboardShortcutBlockList,
		disableDownloadFiles: proctoringAssessmentConfiguration?.disableDownloadFiles,
		applicationBlockMode: proctoringAssessmentConfiguration?.applicationBlockMode,
		blockWindowedApplications: proctoringAssessmentConfiguration?.blockWindowedApplications,
		applicationBlockList: proctoringAssessmentConfiguration?.applicationBlockList,
		allowedApplicationsList: proctoringAssessmentConfiguration?.allowedApplicationsList,
		blockVirtualMachines: proctoringAssessmentConfiguration?.blockVirtualMachines,
		recordApplicationsActivity: proctoringAssessmentConfiguration?.recordApplicationsActivity,
		quietEnvironment: proctoringAssessmentConfiguration?.quietEnvironment,
		enableCalculator: proctoringAssessmentConfiguration?.enableCalculator,
		enableWhiteboard: proctoringAssessmentConfiguration?.enableWhiteboard,
		enableCodeEditor: proctoringAssessmentConfiguration?.enableCodeEditor,
		codeEditorRoomId: proctoringAssessmentConfiguration?.codeEditorRoomId,
		uploadScratchNotes: proctoringAssessmentConfiguration?.uploadScratchNotes,
		idVerificationMode: proctoringAssessmentConfiguration?.idVerificationMode,
		restrictDevices: proctoringAssessmentConfiguration?.restrictDevices,
		uploadSignature: proctoringAssessmentConfiguration?.uploadSignature,
		audioTranscription: proctoringAssessmentConfiguration?.audioTranscription,
		flaggedKeywords: proctoringAssessmentConfiguration?.flaggedKeywords,
		enableGeofence: proctoringAssessmentConfiguration?.enableGeofence,
		geofenceLocations: proctoringAssessmentConfiguration?.geofenceLocations,
		disableVirtualCamera: proctoringAssessmentConfiguration?.disableVirtualCamera,
	};
	return proctoringOptions;
}

export type ConfigurationProctoringOptionData = {
	applications: Application[];
	extensions: Extension[];
	thirdPartyPlatforms: ThirdPartyPlatform[];
};

type ConfigurationPanelProps = {
	proctoringData: ConfigurationProctoringOptionData;
	proctoringAssessmentConfiguration?: Partial<ProctoringAssessmentConfigurationV1>;
	verityInstituteSettings?: VerityInstituteSettings;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
};

export default function ConfigurationPanel({
	proctoringData,
	optionRelatedInstituteSettings,
	proctoringAssessmentConfiguration,
	verityInstituteSettings,
}: ConfigurationPanelProps) {
	const option = convertProctoringAssessmentConfigurationV1toProctoringOption(proctoringAssessmentConfiguration);

	if (!proctoringAssessmentConfiguration) return <CoreLoadingPage size={100} />;

	return (
		<div className={styles.configurationTabpanel}>
			{proctoringAssessmentConfiguration.thirdPartyAssessmentOptions && (
				<ThirdPartyBlock
					thirdPartyOption={proctoringAssessmentConfiguration.thirdPartyAssessmentOptions}
					thirdPartyPlatforms={proctoringData.thirdPartyPlatforms}
				/>
			)}
			<PlatformBlock
				platformText={getPlatformTextFromProctoringOptions({
					...option,
					isChromeOs: option.isChromeOs || proctoringAssessmentConfiguration.forceChromeOsManagedGuestSession,
				})}
			/>
			<ConfigurationBlock
				applications={proctoringData.applications}
				extensions={proctoringData.extensions}
				verityInstituteSettings={verityInstituteSettings}
				optionRelatedInstituteSettings={optionRelatedInstituteSettings}
				proctoringOption={option}
			/>
		</div>
	);
}
